import { Trans } from 'react-i18next';
import i18n from 'locales/i18n';
import { LineChartProps } from '@components/report/chart/line-chart/LineChart';

import { TriangleChartProps } from '@components/report/chart/triangle-chart/TriangleChart';

import { TTReportRes } from '@apis/orval/model';

import round, { roundFloat } from '@utils/round';

import { FollowerGrowthTtProps } from './FollowerGrowth';

export class FollowerTrendAdapter {
	value: TTReportRes;
	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	get followerList() {
		return (
			(this.value?.follow_stats?.trend?.follower_list ?? []).map((v) => Number(v) ?? 0) ??
			[]
		);
	}
	get dateList() {
		return (this.value?.follow_stats?.trend?.date_list ?? []).map((v) => v ?? '') ?? [];
	}
	get difference() {
		return this.followerList[0] - this.followerList[this.followerList.length - 1];
	}

	get isPositive() {
		return this.difference < 0 ? 'high' : this.difference > 0 ? 'low' : 'normal';
	}

	textMapper = {
		high: i18n?.t('infReportTiktok:follower-growth.high'), // ' 성장했습니다.',
		low: i18n?.t('infReportTiktok:follower-growth.low'), //' 하락했습니다.',
		normal: i18n?.t('infReportTiktok:follower-growth.normal'), //'변동이 없습니다.',
	};

	adapt(): LineChartProps {
		if (this.followerList.length === 0) return { isError: true };

		return {
			desc: (
				<>
					{/* 팔로워가{' '}
					<span>
						{this.difference !== 0 ? `${round(Math.abs(this.difference), 'down')} 명` : ''}
						{this.textMapper[this.isPositive]}
					</span> */}
					<Trans
						// t={i18n?.t}
						i18nKey={'infReportTiktok:follower-growth.follower-list.description'}
						components={[
							<span key={'growth'}>
								{this.difference !== 0
									? i18n?.t('unit.number-of-person', {
											number: round(Math.abs(this.difference), 'down'),
									  })
									: ''}
								{this.textMapper[this.isPositive]}
							</span>,
						]}
					/>
				</>
			),
			datas_a: this.followerList,
			categorys: this.dateList,
			isPositive: this.isPositive === 'high',
		};
	}
}

export class FollowerGrowthRateAdapter {
	value: TTReportRes;
	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	get followerGrowthRate() {
		return this.value?.follow_stats?.trend?.follower_growth_rate ?? 0;
	}
	get followerGrowthRateInSimilarGroup() {
		return this.value?.follow_stats?.follower_growth_rate_rank_in_similar_group ?? 0;
	}

	adapt(): TriangleChartProps {
		return {
			desc: (
				<>
					{/* 팔로워 성장률이 <span>{roundFloat(this.followerGrowthRate * 100, 'down', 2)}%</span>로<br />
					유사 그룹 중{' '}
					<span>
						상위{' '}
						{this.followerGrowthRateInSimilarGroup
							? roundFloat(this.followerGrowthRateInSimilarGroup * 100, 'down', 2)
							: '-'}
						%
					</span>
					에 해당됩니다. */}
					<Trans
						// t={i18n?.t}
						i18nKey={
							'infReportTiktok:follower-growth.follower-growth-rate.description.text'
						}
						components={[
							<span key={'follower-growth-rate'}>
								{roundFloat(this.followerGrowthRate * 100, 'down', 2)}%
							</span>,
							<span key={'percentage'}>
								{i18n?.t(
									'infReportTiktok:follower-growth.follower-growth-rate.description.span-text',
									{
										rate: this.followerGrowthRateInSimilarGroup
											? roundFloat(this.followerGrowthRateInSimilarGroup, 'down', 2)
											: '-',
									},
								)}
							</span>,
						]}
					/>
				</>
			),
			valuePercentage: this.followerGrowthRateInSimilarGroup ?? undefined,
		};
	}
}

export class FollowingTrendAdapter {
	value: TTReportRes;
	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	get followingList() {
		return (this.value?.follow_stats?.trend?.following_list ?? []).map(
			(v) => Number(v) ?? 0,
		);
	}
	get dateList() {
		return (this.value?.follow_stats?.trend?.date_list ?? []).map((v) => v ?? '');
	}
	get difference() {
		return this.followingList[0] - this.followingList[this.followingList.length - 1];
	}

	get isPositive() {
		return this.difference < 0 ? 'high' : this.difference > 0 ? 'low' : 'normal';
	}

	textMapper = {
		high: i18n?.t('infReportTiktok:follower-growth.high'), // ' 성장했습니다.',
		low: i18n?.t('infReportTiktok:follower-growth.low'), //' 하락했습니다.',
		normal: i18n?.t('infReportTiktok:follower-growth.normal'), //'변동이 없습니다.',
	};

	adapt(): LineChartProps {
		if (this.followingList.length === 0) return { isError: true };
		return {
			desc: (
				<>
					{/* 팔로잉은{' '}
					<span>
						{this.difference !== 0 ? `${round(Math.abs(this.difference), 'down')} 명` : ''}
						{this.textMapper[this.isPositive]}
					</span> */}
					<Trans
						// t={i18n?.t}
						i18nKey={'infReportTiktok:follower-growth.following-list.description'}
						components={[
							<span key={'growth'}>
								{this.difference !== 0
									? i18n?.t('unit.number-of-person', {
											number: round(Math.abs(this.difference), 'down'),
									  })
									: ''}
								{this.textMapper[this.isPositive]}
							</span>,
						]}
					/>
				</>
			),
			datas_a: this.followingList,
			categorys: this.dateList,
			isPositive: this.isPositive === 'high',
		};
	}
}

export class FollowerGrowthTtAdapter {
	value: TTReportRes;
	constructor(obj: TTReportRes) {
		this.value = obj;
	}

	adapt(): FollowerGrowthTtProps {
		return {
			followerTrend: new FollowerTrendAdapter(this.value).adapt(),
			followerGrowthRate: new FollowerGrowthRateAdapter(this.value).adapt(),
			followingTrend: new FollowingTrendAdapter(this.value).adapt(),
		};
	}
}
